import WellKnown from '@/objects/generated/static/WellKnown';
import { useContestantStore, useUtilStore } from '@/stores';
import { ref, type Ref } from 'vue';
import { useEventCommonStoreRef, useToastStore } from '@/stores';
import { addTimespanToDate } from '@/utilities/dateutils';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import MultiEditor from '@/components/MultiEditor/MultiEditor.vue';
import { createIntField, getFieldValue, createFileField, MultiEditorField } from '@/components/MultiEditor/MultiEditorField';
import { InputTimespan, InputNullableInt, InputSelect, InputNullableDateTime, InputNullableTimespan } from '@/components';
import { CopyStartTimesRequest, StartingNumber, StartingNumberSetFlagRequest, type ExitStatus } from '@/objects/generated';
import type BaseObjListViewCompExpose from '@/objects/BaseObjListViewCompExpose';
import { useDialog } from './dialog/dialog';
import { type SelectLineValueType } from '@/objects';
import { StartingNumberDao } from '@/dao';
import { useTaskLoader } from './loaders';
import { range } from '@/utilities';
import { resolveablePromise } from '@/utilities/promiseutils';

export const useBibsActions = (grid: Ref<BaseObjListViewCompExpose<StartingNumber> | null>, Bibs: Ref<Array<StartingNumber>>) => {
    const { Event } = useEventCommonStoreRef();

    // Helper for chip assignment logic
    const assignChipToBibs = (selected: StartingNumber[], chipSlot: string, startChip: number, skipExisting: boolean = false): void => {
        let chip = startChip;
        selected.forEach((bib) => {
            if (chipSlot === 'chip1') {
                if (skipExisting) {
                    if (!bib.Chip1) {
                        bib.Chip1 = chip.toString();
                        chip++;
                    }
                } else {
                    bib.Chip1 = chip.toString();
                    chip++;
                }
            } else if (chipSlot === 'chip2') {
                if (skipExisting) {
                    if (!bib.Chip2) {
                        bib.Chip2 = chip.toString();
                        chip++;
                    }
                } else {
                    bib.Chip2 = chip.toString();
                    chip++;
                }
            }
        });
    };

    // Helper functions for dialog fields
    const createChipSlotField = (defaultValue: string = 'chip1') => {
        return new MultiEditorField<SelectLineValueType>({
            translationKey: 'bibs.chip_slot',
            value: defaultValue,
            editor: InputSelect,
            params: {
                datasource: {
                    source: [
                        { value: 'chip1', text: 'bibs.chip1' },
                        { value: 'chip2', text: 'bibs.chip2' },
                    ],
                },
            },
        });
    };

    const createFirstChipField = (defaultValue: number) => {
        return new MultiEditorField<number>({
            translationKey: 'bibs.first_chip',
            value: defaultValue,
            editor: InputNullableInt,
            params: {},
        });
    };

    const createOffsetField = (defaultValue: number) => {
        return createIntField({ defaultValue, translationKey: 'bibs.offset' });
    };

    const createLaneFields = (firstLane: number, lastLane: number): [string, MultiEditorField<number>][] => {
        return [
            [
                'FirstLane',
                new MultiEditorField<number>({
                    translationKey: 'bibs.first_lane',
                    value: firstLane,
                    editor: InputNullableInt,
                    params: {},
                }),
            ],
            [
                'LastLane',
                new MultiEditorField<number>({
                    translationKey: 'bibs.last_lane',
                    value: lastLane,
                    editor: InputNullableInt,
                    params: {},
                }),
            ],
        ];
    };

    const createTimespanField = () => {
        return new MultiEditorField<ITimeSpan>({
            translationKey: 'bibs.timespan_to_move',
            value: TimeSpan.Empty(),
            editor: InputTimespan,
            params: {},
        });
    };

    // Helper for selection check
    const getSelectedBibsOrInfo = (infoKey: string, filter?: (sn: StartingNumber) => boolean) => {
        let selected = grid.value?.getSelected();
        if (filter) {
            selected = selected?.filter(filter);
        }
        if (!selected || selected.length === 0) {
            useToastStore().addInfo(infoKey);
            return null;
        }
        return selected;
    };

    // Copy starttime to contestant
    const copyStartTimeToContestants = async (): Promise<void> => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_copy_starttime');
        if (!selected) return;

        const { AddLoader, LoadState } = useTaskLoader();

        useDialog().YesNo({
            title: 'bibs.copy_starttime_to_contestant',
            body: 'bibs.confirm_copy_starttime_to_contestant',
            savestate: LoadState.value,
            yesCallback: async (): Promise<boolean> => {
                const eventID = typeof Event.value?.ID === 'number' ? Event.value.ID : 0;
                const bibIDs = selected.map((bib: StartingNumber) => bib.ID);
                const request = CopyStartTimesRequest.FromJsValues({ StartNumberIds: bibIDs });
                let ret = false;
                await AddLoader(async () => {
                    const resp = await StartingNumberDao.CopyStartTimesToContestants(eventID, request);
                    if (resp?.Success) {
                        useToastStore().addSuccess('bibs.copy_starttime_success');
                        ret = true;
                    } else {
                        useToastStore().addError('bibs.copy_starttime_failed');
                    }
                }, '');
                return ret;
            },
        });
    };

    // Copy starttime from contestant
    const copyStartTimeFromContestants = async (): Promise<void> => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_copy_starttime');
        if (!selected) return;

        const { AddLoader, LoadState } = useTaskLoader();

        useDialog().YesNo({
            title: 'bibs.copy_starttime_from_contestant',
            body: 'bibs.confirm_copy_starttime_from_contestant',
            savestate: LoadState.value,
            yesCallback: async (): Promise<boolean> => {
                const eventID = typeof Event.value?.ID === 'number' ? Event.value.ID : 0;
                const bibIDs = selected.map((bib: StartingNumber) => bib.ID);
                const request = CopyStartTimesRequest.FromJsValues({ StartNumberIds: bibIDs });
                let ret = false;
                await AddLoader(async () => {
                    const resp = await StartingNumberDao.CopyStartTimesFromContestant(eventID, request);
                    if (resp?.Success) {
                        useToastStore().addSuccess('bibs.copy_starttime_success');
                        grid.value?.refreshList(resp.IDS!);
                        ret = true;
                    } else {
                        useToastStore().addError('bibs.copy_starttime_failed');
                    }
                }, '');
                return ret;
            },
        });
    };

    // Block selected bibs
    const blockSelected = async (): Promise<void> => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_block');
        if (!selected) return;
        useDialog().YesNo({
            title: 'bibs.block_selected_bibs',
            body: 'bibs.confirm_block_selected_bibs',
            yesCallback: async (): Promise<boolean> => {
                selected.forEach((bib: StartingNumber) => {
                    bib.IsBlocked = true;
                });
                return true;
            },
        });
    };

    // Unblock selected bibs
    const unblockSelected = async (): Promise<void> => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_unblock');
        if (!selected) return;
        useDialog().YesNo({
            title: 'bibs.unblock_selected_bibs',
            body: 'bibs.confirm_unblock_selected_bibs',
            yesCallback: async (): Promise<boolean> => {
                selected.forEach((bib: StartingNumber) => {
                    bib.IsBlocked = false;
                });
                return true;
            },
        });
    };
    // Remove lane (Target) from selected bibs with confirmation dialog
    const removeLaneOnSelected = async (): Promise<void> => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_remove_lane');
        if (!selected) return;
        useDialog().YesNo({
            title: 'bibs.remove_lane_selected_bibs',
            body: 'bibs.confirm_remove_lane_selected_bibs',
            yesCallback: async (): Promise<boolean> => {
                selected.forEach((bib: StartingNumber) => {
                    bib.Target = null;
                });
                return true;
            },
        });
    };

    // Remove chip from selected bibs
    const removeChipFromSelected = () => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_remove_chip');
        if (!selected) return;
        const fields = ref(new Map<string, MultiEditorField<SelectLineValueType>>([['ChipSlot', createChipSlotField()]]));
        useDialog().YesNo({
            title: 'bibs.remove_chip_from_selected',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.remove_chip_from_selected_info' },
            yesCallback: async () => {
                const chipSlot = getFieldValue<SelectLineValueType>(fields.value, 'ChipSlot');
                if (chipSlot !== 'chip1' && chipSlot !== 'chip2') {
                    useToastStore().addError('bibs.invalid_chip_or_slot');
                    return false;
                }
                selected.forEach((bib) => {
                    if (chipSlot === 'chip1') {
                        bib.Chip1 = null;
                    } else if (chipSlot === 'chip2') {
                        bib.Chip2 = null;
                    }
                });
                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // Set EMIT tag to selected bibs
    const setEmitTagOnSelected = async (): Promise<void> => {
        const selected = grid.value?.getSelected();
        if (!selected || selected.length === 0) {
            useToastStore().addInfo('bibs.no_bibs_selected_to_set_emittag');
            return;
        }
        const fields = ref(new Map<string, MultiEditorField<any>>([['ChipSlot', createChipSlotField()]]));
        useDialog().YesNo({
            title: 'bibs.set_emittag_to_selected',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.set_emittag_to_selected_info' },
            yesCallback: async () => {
                const chipSlot = getFieldValue<string>(fields.value, 'ChipSlot');
                if (chipSlot !== 'chip1' && chipSlot !== 'chip2') {
                    useToastStore().addError('bibs.invalid_chip_or_slot');
                    return false;
                }
                let updated = false;
                const contestantStore = useContestantStore();
                selected.forEach((bib: StartingNumber) => {
                    if (bib.ContestantID) {
                        const contestant = contestantStore.GetContestantById(bib.ContestantID);
                        if (contestant) {
                            const id = contestant.ParticipantIDs.find((id) => id.ParticipantIDTypeID == WellKnown.IDTYPE_EMIT);
                            if (!id || !id.Value) {
                                return;
                            }
                            if (chipSlot === 'chip1') {
                                bib.Chip1 = id.Value.toString();
                                updated = true;
                            } else if (chipSlot === 'chip2') {
                                bib.Chip2 = id.Value.toString();
                                updated = true;
                            }
                        }
                    }
                });
                if (!updated) {
                    useToastStore().addInfo('bibs.no_emittag_found_for_selected');
                    return false;
                }
                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // Set chip as offset from bib
    const setChipAsOffsetFromBibOnSelected = async () => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_set_chip_offset');
        if (!selected) return;
        const fields = ref(
            new Map<string, MultiEditorField<any>>([
                ['Offset', createOffsetField(1)],
                ['ChipSlot', createChipSlotField()],
            ]),
        );
        useDialog().YesNo({
            title: 'bibs.set_chip_as_offset_from_bib',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.set_chip_as_offset_from_bib_info' },
            yesCallback: async () => {
                const offset = getFieldValue<number>(fields.value, 'Offset') ?? 0;
                const chipSlot = getFieldValue<string>(fields.value, 'ChipSlot');
                if (chipSlot !== 'chip1' && chipSlot !== 'chip2') {
                    useToastStore().addError('bibs.invalid_chip_or_slot');
                    return false;
                }
                selected.forEach((bib) => {
                    const chipValue = bib.Bib + offset;
                    if (chipSlot === 'chip1') {
                        bib.Chip1 = chipValue.toString();
                    } else if (chipSlot === 'chip2') {
                        bib.Chip2 = chipValue.toString();
                    }
                });
                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // Create bibs for a range of bib numbers
    const createBibsFromTo = async () => {
        const fields = ref(
            new Map<string, MultiEditorField<any>>([
                ['From', createIntField({ defaultValue: 1, translationKey: 'bibs.from', min: 1, max: null, required: true })],
                ['To', createIntField({ defaultValue: 1000, translationKey: 'bibs.to', min: 1, max: null, required: true })],
            ]),
        );
        useDialog().YesNo({
            title: 'bibs.create_bibs_from_to',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.create_bibs_from_to_info' },
            yesCallback: async () => {
                const from = getFieldValue<number>(fields.value, 'From') ?? 0;
                const to = getFieldValue<number>(fields.value, 'To') ?? 0;
                // find all we don't have in bibs and create them
                const existingBibs = new Set(Bibs.value.map((b) => b.Bib));
                const newBibs: StartingNumber[] = [];
                for (let i = from; i <= to; i++) {
                    if (!existingBibs.has(i)) {
                        newBibs.push(
                            StartingNumber.FromJsValues({
                                Bib: i,
                                ID: useUtilStore().nextFakeId(),
                                EventID: Event.value?.ID ?? 0,
                                EntityState: 'Added',
                            }),
                        );
                    }
                }
                if (newBibs.length === 0) {
                    useToastStore().addInfo('bibs.no_new_bibs_to_create');
                    return false;
                }
                Bibs.value.push(...newBibs);
                grid.value?.dataUpdated();

                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // upload file with start times and assign to bibs
    const uploadFileWithStartTimes = async () => {
        const fields = ref(new Map<string, MultiEditorField<File | null>>([['File', createFileField('bibs.file_with_starttimes')]]));
        useDialog().YesNo({
            title: 'bibs.upload_file_with_starttimes',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.upload_file_with_starttimes_info' },
            yesCallback: async () => {
                const file = getFieldValue<File | null>(fields.value, 'File');
                if (!file) {
                    useToastStore().addInfo('bibs.no_file_selected');
                    return false;
                }
                const date = Event.value!.Date!;
                const reader = new FileReader();
                let count = 0;
                const prom = resolveablePromise<void>();
                reader.onload = (e) => {
                    const text = e.target?.result;
                    if (typeof text === 'string') {
                        const lines = text.split('\n');
                        // lines are expected to be in format "bib;starttime" where starttime HH:MM:SS or HH:MM:SS.mmm
                        // it can also be headings and empty so we need to check if we can parse the bib and starttime before assigning
                        for (const line of lines) {
                            const cols = line.split(';');
                            if (cols.length >= 2) {
                                const bib = parseInt(cols[0]);
                                if (isNaN(bib)) {
                                    continue;
                                }
                                // check starttime column is in correct format by trying to parse it to a timespan
                                try {
                                    const startTime = addTimespanToDate(TimeSpan.FromTimeString(cols[1]), date);
                                    const bibToUpdate = Bibs.value.find((b) => b.Bib === bib);
                                    if (bibToUpdate) {
                                        bibToUpdate.StartTime = startTime;
                                        count++;
                                    }
                                } catch (error) {
                                    console.warn(`Error parsing start time for bib ${bib} with value ${cols[1]}:`, error);
                                }
                            }
                        }
                    }
                    prom.resolver();
                };
                reader.readAsText(file);
                await prom.promise;
                if (count === 0) {
                    useToastStore().addWarning('bibs.no_valid_start_times_found');
                } else {
                    grid.value?.dataUpdated();
                }

                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // upload file with chips and assign to bibs
    const uploadFileWithChips = async () => {
        const fields = ref(
            new Map<string, MultiEditorField<any>>([
                ['File', createFileField('bibs.file_with_chips')],
                ['ChipSlot', createChipSlotField()],
            ]),
        );
        useDialog().YesNo({
            title: 'bibs.upload_file_with_chips',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.upload_file_with_chips_info' },
            yesCallback: async () => {
                const file = getFieldValue<File | null>(fields.value, 'File');
                const chipSlot = getFieldValue<string>(fields.value, 'ChipSlot');
                if (!file) {
                    useToastStore().addInfo('bibs.no_file_selected');
                    return false;
                }
                const reader = new FileReader();
                let count = 0;
                const prom = resolveablePromise<void>();
                reader.onload = (e) => {
                    const text = e.target?.result;
                    if (typeof text === 'string') {
                        const lines = text.split('\n');
                        // lines are expected to be in format "bib;chip" where chip is the chip ID
                        // it can also be headings and empty so we need to check if we can parse the bib and chip before assigning
                        for (const line of lines) {
                            const cols = line.split(';');
                            if (cols.length >= 2) {
                                const bib = parseInt(cols[0]);
                                if (isNaN(bib)) {
                                    continue;
                                }
                                // check chip column is in correct format by trying to parse it to a number
                                try {
                                    const bibToUpdate = Bibs.value.find((b) => b.Bib === bib);
                                    if (bibToUpdate) {
                                        if (chipSlot === 'chip1') {
                                            bibToUpdate.Chip1 = cols[1].trim();
                                        } else if (chipSlot === 'chip2') {
                                            bibToUpdate.Chip2 = cols[1].trim();
                                        }
                                        count++;
                                    }
                                } catch (error) {
                                    console.warn(`Error parsing chip for bib ${bib} with value ${cols[1]}:`, error);
                                }
                            }
                        }
                    }
                    prom.resolver();
                };
                reader.readAsText(file);
                await prom.promise;
                if (count === 0) {
                    useToastStore().addWarning('bibs.no_valid_chips_found');
                } else {
                    grid.value?.dataUpdated();
                }

                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // upload file with participants, it expects a csv with participantid;bib;any_data
    const uploadFileWithParticipants = async () => {
        const fields = ref(new Map<string, MultiEditorField<File | null>>([['File', createFileField('bibs.file_with_participants')]]));
        useDialog().YesNo({
            title: 'bibs.upload_file_with_participants',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.upload_file_with_participants_info' },
            yesCallback: async () => {
                const file = getFieldValue<File | null>(fields.value, 'File');
                if (!file) {
                    useToastStore().addInfo('bibs.no_file_selected');
                    return false;
                }
                const reader = new FileReader();
                const alreadyhascontestantAssigned: number[] = [];
                const alreadyhasbibAssigned: number[] = [];
                const toAssign: { bib: StartingNumber; contestantID: number }[] = [];
                const prom = resolveablePromise<void>();
                reader.onload = (e) => {
                    const text = e.target?.result;
                    if (typeof text === 'string') {
                        const lines = text.split('\n');
                        // lines are expected to be in format "participantid;bib;any_data"
                        // it can also be headings and empty so we need to check if we can parse the participantid and bib before assigning
                        for (const line of lines) {
                            const cols = line.split(';');
                            if (cols.length >= 2) {
                                const bib = parseInt(cols[1]);
                                if (isNaN(bib)) {
                                    continue;
                                }
                                const participantID = parseInt(cols[0]);
                                if (isNaN(participantID)) {
                                    continue;
                                }
                                const bibToUpdate = Bibs.value.find((b) => b.Bib === bib);
                                if (bibToUpdate) {
                                    if (bibToUpdate.ContestantID != null) {
                                        alreadyhascontestantAssigned.push(bib);
                                        continue;
                                    }
                                    // find contestant
                                    const contestant = useContestantStore().GetContestantByParticipantId(participantID);
                                    if (contestant) {
                                        if (contestant.Bib != null && contestant.Bib != bib) {
                                            alreadyhasbibAssigned.push(bib);
                                            continue;
                                        }
                                        toAssign.push({ bib: bibToUpdate, contestantID: contestant.ID });
                                    }
                                }
                            }
                        }
                    }

                    prom.resolver();
                };
                reader.readAsText(file);
                await prom.promise;
                if (alreadyhascontestantAssigned.length > 0) {
                    useToastStore().addWarning('bibs.some_participants_already_assigned', {
                        bibs: alreadyhascontestantAssigned.join(', '),
                    });
                    return false;
                }
                if (alreadyhasbibAssigned.length > 0) {
                    useToastStore().addWarning('bibs.some_bibs_already_assigned', { bibs: alreadyhasbibAssigned.join(', ') });
                    return false;
                }
                if (toAssign.length === 0) {
                    useToastStore().addWarning('bibs.no_valid_participants_found');
                } else {
                    toAssign.forEach((a) => {
                        a.bib.ContestantID = a.contestantID;
                    });
                    grid.value?.dataUpdated();
                }

                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // Create bibs from categories and groups
    const createBibsFromCategoriesOrGroups = async () => {
        useDialog().YesNo({
            title: 'bibs.create_bibs_from_categories_or_groups',
            body: 'bibs.create_bibs_from_categories_or_groups_info',
            yesCallback: async () => {
                const categories = useEventCommonStoreRef().Categories;
                const groups = useEventCommonStoreRef().Groups;
                const ranges = [
                    ...categories.value.map((c) => range(Math.max(c.BibFrom ?? 0, 1), Math.max(c.BibTo ?? 0, 1))),
                    ...groups.value.map((g) => range(Math.max(g.BibFrom ?? 0, 1), Math.max(g.BibTo ?? 0, 1))),
                ];
                // merge ranges
                const bibsFromRanges = new Set<number>();
                ranges.forEach((r) => {
                    r.forEach((bib) => bibsFromRanges.add(bib));
                });

                // find all we don't have in bibs and create them
                const existingBibs = new Set(Bibs.value.map((b) => b.Bib));
                const newBibs: StartingNumber[] = [];
                for (const bib of bibsFromRanges) {
                    if (!existingBibs.has(bib)) {
                        newBibs.push(
                            StartingNumber.FromJsValues({
                                Bib: bib,
                                ID: useUtilStore().nextFakeId(),
                                EventID: Event.value?.ID ?? 0,
                                EntityState: 'Added',
                            }),
                        );
                    }
                }
                if (newBibs.length === 0) {
                    useToastStore().addInfo('bibs.no_new_bibs_to_create');
                    return false;
                }
                Bibs.value.push(...newBibs);
                grid.value?.dataUpdated();

                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // Action: Assign chip to marked bibs that are missing chip assignment
    const assignChipOnSelectedWhenMissingchip = async (): Promise<void> => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_assign_chip');
        if (!selected) return;
        // Ask for first chip and chip slot (chip1 or chip2)
        const fields = ref(
            new Map<string, MultiEditorField<any>>([
                ['FirstChip', createFirstChipField(1)],
                ['ChipSlot', createChipSlotField()],
            ]),
        );

        useDialog().YesNo({
            title: 'bibs.assign_chip_selected_bibs_when_missing',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.assign_chip_selected_bibs_when_missing_info' },
            yesCallback: async () => {
                const chip = getFieldValue<number>(fields.value, 'FirstChip');
                const chipSlot = getFieldValue<string>(fields.value, 'ChipSlot');
                if (!chip || (chipSlot !== 'chip1' && chipSlot !== 'chip2')) {
                    useToastStore().addError('bibs.invalid_chip_or_slot');
                    return false;
                }
                assignChipToBibs(selected, chipSlot, chip, true);
                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // Action: Assign chip to marked bibs
    const assignChipOnSelected = async (): Promise<void> => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_assign_chip');
        if (!selected) return;
        // Ask for first chip and chip slot (chip1 or chip2)
        const fields = ref(
            new Map<string, MultiEditorField<any>>([
                ['FirstChip', createFirstChipField(1)],
                ['ChipSlot', createChipSlotField()],
            ]),
        );

        useDialog().YesNo({
            title: 'bibs.assign_chip_selected_bibs',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.assign_chip_selected_bibs_info' },
            yesCallback: async () => {
                const chip = getFieldValue<number>(fields.value, 'FirstChip');
                const chipSlot = getFieldValue<string>(fields.value, 'ChipSlot');
                if (!chip || (chipSlot !== 'chip1' && chipSlot !== 'chip2')) {
                    useToastStore().addError('bibs.invalid_chip_or_slot');
                    return false;
                }
                assignChipToBibs(selected, chipSlot, chip, false);
                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // Action: Set starttime on selected bibs
    const setStartTimeOnSelected = async () => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_set_starttime');
        if (!selected) return;
        const fields = ref(
            new Map<string, MultiEditorField<any>>([
                [
                    'StartTime',
                    new MultiEditorField<Date>({
                        translationKey: 'bibs.starttime',
                        value: null,
                        editor: InputNullableDateTime,
                        params: { format: 'DATETIMEMS3', prefillDate: Event.value?.Date ?? null },
                    }),
                ],
                [
                    'Interval',
                    new MultiEditorField<ITimeSpan>({
                        translationKey: 'bibs.interval',
                        value: null,
                        editor: InputNullableTimespan,
                    }),
                ],
                [
                    'SimultaneousStarters',
                    new MultiEditorField<number>({
                        translationKey: 'bibs.simultaneous_starters',
                        value: null,
                        editor: InputNullableInt,
                        params: {},
                    }),
                ],
            ]),
        );

        useDialog().YesNo({
            title: 'bibs.set_starttime_selected_bibs',
            body: MultiEditor,
            vmodel: fields,
            yesCallback: async () => {
                let st = getFieldValue<Date>(fields.value, 'StartTime') || null;
                const interval = getFieldValue<ITimeSpan>(fields.value, 'Interval') || TimeSpan.Empty();
                const simultaneousStarters = getFieldValue<number>(fields.value, 'SimultaneousStarters') || 1;
                let simcurrent = 0;
                selected.forEach((bib) => {
                    bib.StartTime = st;
                    simcurrent++;
                    if (interval && interval.TotalMilliseconds > 0 && simcurrent >= simultaneousStarters) {
                        st = st ? addTimespanToDate(interval, st) : null;
                        simcurrent = 0;
                    }
                });

                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // Action: Assign lane to selected bibs
    const assignLaneToSelected = async () => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_assign_lane');
        if (!selected) return;
        const fields = ref(new Map<string, MultiEditorField<number>>(createLaneFields(1, 30)));

        useDialog().YesNo({
            title: 'bibs.assign_lane_selected_bibs',
            body: MultiEditor,
            vmodel: fields,
            yesCallback: async () => {
                const firstLane = getFieldValue<number>(fields.value, 'FirstLane') ?? 1;
                const lastLane = getFieldValue<number>(fields.value, 'LastLane') ?? firstLane;
                if (isNaN(firstLane) || isNaN(lastLane) || firstLane > lastLane) {
                    useToastStore().addError('bibs.invalid_lane_range');
                    return false;
                }
                let lane = firstLane;
                selected.forEach((bib) => {
                    bib.Target = lane;
                    lane++;
                    if (lane > lastLane) lane = firstLane;
                });

                return true;
            },
            options: { Size: 'lg' },
        });
    };

    const unassignContestantsOnSelected = async () => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_unassign');
        if (!selected) return;
        useDialog().YesNo({
            title: 'bibs.unassign_selected_bibs',
            body: 'bibs.confirm_unassign_selected_bibs',
            yesCallback: async () => {
                selected.forEach((bib) => {
                    bib.ContestantID = null;
                });

                return true;
            },
        });
    };

    const removeStartTimeOnSelected = async () => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_remove_starttime');
        if (!selected) return;
        useDialog().YesNo({
            title: 'bibs.remove_starttime_selected_bibs',
            body: 'bibs.confirm_remove_starttime_selected_bibs',
            yesCallback: async () => {
                selected.forEach((bib) => {
                    bib.StartTime = null;
                });

                return true;
            },
        });
    };

    // Move start times for marked bibs by a user-specified timespan
    const moveStartTimesOnSelected = async () => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_move_starttime');
        if (!selected) return;
        const fields = ref(new Map<string, MultiEditorField<ITimeSpan>>([['Timespan', createTimespanField()]]));
        useDialog().YesNo({
            title: 'bibs.move_starttimes_for_marked',
            body: MultiEditor,
            vmodel: fields,
            componentProps: { info: 'bibs.move_starttimes_for_marked_info' },
            yesCallback: async () => {
                const timespan = getFieldValue<ITimeSpan>(fields.value, 'Timespan');
                if (!timespan || timespan.TotalMilliseconds === 0) {
                    useToastStore().addError('bibs.invalid_timespan');
                    return false;
                }
                selected.forEach((bib) => {
                    if (bib.StartTime) {
                        const date = typeof bib.StartTime === 'string' ? new Date(bib.StartTime) : bib.StartTime;
                        bib.StartTime = addTimespanToDate(timespan, date);
                    }
                });

                return true;
            },
            options: { Size: 'lg' },
        });
    };

    // set flags on selected bibs
    const setExitStatus = async (status: ExitStatus) => {
        const selected = getSelectedBibsOrInfo('bibs.no_bibs_selected_to_set_exit_status', (sn) => sn.ContestantID != null);

        if (!selected) return;

        const { AddLoader, LoadState } = useTaskLoader();

        useDialog().YesNo({
            title: 'bibs.set_exit_status_on_selected',
            body: 'bibs.confirm_set_exit_status_on_selected',
            savestate: LoadState.value,
            yesCallback: async (): Promise<boolean> => {
                const eventID = typeof Event.value?.ID === 'number' ? Event.value.ID : 0;
                const bibIDs = selected.map((bib: StartingNumber) => bib.ID);
                const request = StartingNumberSetFlagRequest.FromJsValues({ StartNumberIds: bibIDs, ExitStatus: status });
                let ret = false;
                await AddLoader(async () => {
                    const resp = await StartingNumberDao.SetFlagsToContestants(eventID, request);
                    if (resp?.Success) {
                        useToastStore().addSuccess('bibs.set_exit_status_success');
                        ret = true;
                    } else {
                        useToastStore().addError('bibs.set_exit_status_failed');
                    }
                }, '');
                return ret;
            },
        });
    };

    return {
        setStartTimeOnSelected,
        assignLaneToSelected,
        unassignContestantsOnSelected,
        assignChipOnSelectedWhenMissingchip,
        assignChipOnSelected,
        getSelectedBibsOrInfo,
        moveStartTimesOnSelected,
        removeStartTimeOnSelected,
        setChipAsOffsetFromBibOnSelected,
        removeChipFromSelected,
        removeLaneOnSelected,
        blockSelected,
        unblockSelected,
        setEmitTagOnSelected,
        copyStartTimeToContestants,
        copyStartTimeFromContestants,
        setExitStatus,
        createBibsFromTo,
        createBibsFromCategoriesOrGroups,
        uploadFileWithStartTimes,
        uploadFileWithParticipants,
        uploadFileWithChips,
    };
};
